import { Box, IconButton, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import { TOrganisationCodeWorkflowType } from '../OrganisationCodeWorkflowSwitcher/OrganisationCodeWorkflowSwitcher';
import { useState } from 'react';
import { WorkflowButton } from '../WorkflowButton';

const WORKFLOW_LABELS: Record<TOrganisationCodeWorkflowType, string> = {
  DEFAULT: 'DEFAULT',
  INDIVIDUAL_ONLY: 'INDIVIDUAL ONLY',
  ORGANISATION_ONLY: 'ORGANISATION ONLY',
};

export interface WorkflowSectionProps {
  selectedWorkflow: TOrganisationCodeWorkflowType;
  onWorkflowChange: (workflow: TOrganisationCodeWorkflowType) => Promise<void>;
  isSaving: boolean;
  disabledWorkflows: Record<TOrganisationCodeWorkflowType, boolean>;
  dataTestId?: string;
}

export function WorkflowSection({
  selectedWorkflow,
  onWorkflowChange,
  isSaving,
  disabledWorkflows,
  dataTestId = 'workflow-section',
}: Readonly<WorkflowSectionProps>) {
  const [isEditing, setIsEditing] = useState(false);

  const handleWorkflowClick = async (workflow: TOrganisationCodeWorkflowType) => {
    try {
      await onWorkflowChange(workflow);
      setIsEditing(false);
    } catch (error) {
      // Error already handled by parent
    }
  };

  return (
    <Box data-testid={dataTestId}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.semantic.spacing.sm,
        })}
      >
        <Typography variant="subtitle1">Workflow</Typography>
        {!isEditing && (
          <IconButton
            size="small"
            onClick={() => setIsEditing(true)}
            data-action="edit-workflow"
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {!isEditing ? (
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            padding: theme.semantic.spacing.sm,
            borderRadius: theme.semantic.borderRadius.sm,
          })}
        >
          <Typography
            variant="body1"
            sx={(theme) => ({ fontWeight: theme.semantic.fontWeight.semibold })}
          >
            {WORKFLOW_LABELS[selectedWorkflow]}
          </Typography>
        </Box>
      ) : (
        <Box>
          <Typography
            variant="caption"
            sx={(theme) => ({
              display: 'block',
              marginBottom: theme.semantic.spacing.xs,
              color: theme.palette.text.secondary,
            })}
          >
            Select a workflow type (changes are saved immediately)
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'stretch' }}>
            <WorkflowButton
              workflow="DEFAULT"
              isSelected={selectedWorkflow === 'DEFAULT'}
              label={WORKFLOW_LABELS.DEFAULT}
              onClick={handleWorkflowClick}
              disabled={isSaving || disabledWorkflows.DEFAULT}
            />
            <WorkflowButton
              workflow="INDIVIDUAL_ONLY"
              isSelected={selectedWorkflow === 'INDIVIDUAL_ONLY'}
              label={WORKFLOW_LABELS.INDIVIDUAL_ONLY}
              onClick={handleWorkflowClick}
              disabled={isSaving || disabledWorkflows.INDIVIDUAL_ONLY}
            />
            <WorkflowButton
              workflow="ORGANISATION_ONLY"
              isSelected={selectedWorkflow === 'ORGANISATION_ONLY'}
              label={WORKFLOW_LABELS.ORGANISATION_ONLY}
              onClick={handleWorkflowClick}
              disabled={isSaving || disabledWorkflows.ORGANISATION_ONLY}
            />
            <IconButton
              size="small"
              onClick={() => setIsEditing(false)}
              data-action="cancel-workflow-edit"
              sx={{ color: 'text.secondary' }}
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
